import React, { useState, useEffect, useCallback } from 'react';
import { Container, Card, Button, Spinner, Alert, Form, Table, Badge, Pagination, InputGroup, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaRegCopy, FaCheck, FaSearch } from 'react-icons/fa';
import VereinbarungService from '../services/vereinbarung.service';

const PAGE_SIZE = 15;

const BoardPlaceholderSync = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);

    // Tabellen-/Listenzustand
    const [placeholders, setPlaceholders] = useState([]);
    const [listLoading, setListLoading] = useState(false);
    const [listError, setListError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [reloadFlag, setReloadFlag] = useState(0);
    const [copiedKey, setCopiedKey] = useState('');

    // Suche entprellen
    useEffect(() => {
        const t = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setCurrentPage(1);
        }, 400);
        return () => clearTimeout(t);
    }, [searchTerm]);

    const loadPlaceholders = useCallback(() => {
        setListLoading(true);
        setListError('');
        const params = { page: currentPage, size: PAGE_SIZE, search: debouncedSearch };
        VereinbarungService.getAllPlaceholders(params)
            .then(response => {
                const data = response.data || {};
                setPlaceholders(Array.isArray(data.placeholders) ? data.placeholders : []);
                setTotalPages(data.totalPages || 0);
                setTotalCount(
                    data.totalItems != null ? data.totalItems
                        : (data.totalCount != null ? data.totalCount : (data.placeholders ? data.placeholders.length : 0))
                );
            })
            .catch(() => setListError('Platzhalter konnten nicht geladen werden.'))
            .finally(() => setListLoading(false));
    }, [currentPage, debouncedSearch]);

    useEffect(() => {
        loadPlaceholders();
    }, [loadPlaceholders, reloadFlag]);

    const handleSync = () => {
        setLoading(true);
        setError('');
        setResult(null);

        VereinbarungService.synchronizePlaceholders()
            .then(response => {
                setResult(response.data);
                // Nach der Synchronisierung die Tabelle neu laden.
                setCurrentPage(1);
                setReloadFlag(prev => prev + 1);
            })
            .catch(err => {
                const errorMessage = err.response?.data?.message || "Ein unbekannter Fehler ist aufgetreten.";
                setError(errorMessage);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleCopy = async (key) => {
        try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(key);
            } else {
                // Fallback fuer aeltere Browser
                const ta = document.createElement('textarea');
                ta.value = key;
                ta.style.position = 'fixed';
                ta.style.opacity = '0';
                document.body.appendChild(ta);
                ta.select();
                document.execCommand('copy');
                document.body.removeChild(ta);
            }
            setCopiedKey(key);
            setTimeout(() => setCopiedKey(prev => (prev === key ? '' : prev)), 1500);
        } catch (e) {
            setListError('Kopieren nicht möglich. Bitte manuell markieren.');
        }
    };

    return (
        <Container>
            <h1>Platzhalter-Synchronisierung</h1>
            <Card>
                <Card.Body>
                    <Card.Title>Datenbank-Platzhalter aktualisieren</Card.Title>
                    <Card.Text>
                        Mit dieser Funktion können Sie die Platzhalter-Tabelle mit allen verfügbaren Feldern aus den Datenbank-Tabellen synchronisieren. Das System scannt die Datenbankstruktur und fügt alle neuen, noch nicht vorhandenen Felder als Platzhalter hinzu.
                        <br />
                        <strong>Achtung:</strong> Dieser Vorgang kann je nach Größe der Datenbank einen Moment dauern.
                    </Card.Text>

                    <Button
                        variant="primary"
                        disabled={loading}
                        onClick={handleSync}
                    >
                        {loading ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                {' '}Synchronisiere...
                            </>
                        ) : (
                            'Synchronisierung starten'
                        )}
                    </Button>

                    <div className="mt-3">
                        {error && <Alert variant="danger">{error}</Alert>}
                        {result && (
                            <Alert variant="success">
                                {result.message}
                                <hr />
                                <p className="mb-0">
                                    Neu hinzugefügte Platzhalter: <strong>{result.newPlaceholders}</strong>
                                    <br />
                                    Gesamtzahl der Platzhalter: <strong>{result.totalPlaceholders}</strong>
                                </p>
                            </Alert>
                        )}
                    </div>
                </Card.Body>
            </Card>

            {/* Tabelle: alle erstellten Platzhalter mit Suche und Kopier-Icon */}
            <Card className="mt-4">
                <Card.Body>
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                        <Card.Title className="mb-0">
                            Verfügbare Platzhalter{totalCount ? <span className="text-muted"> ({totalCount})</span> : null}
                        </Card.Title>
                        <InputGroup style={{ maxWidth: 360 }}>
                            <InputGroup.Text><FaSearch /></InputGroup.Text>
                            <Form.Control
                                type="text"
                                placeholder="Suche nach Name oder Schlüssel..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </InputGroup>
                    </div>

                    {listError && <Alert variant="danger">{listError}</Alert>}

                    {listLoading ? (
                        <div className="text-center p-4">
                            <Spinner animation="border" variant="primary" />
                        </div>
                    ) : (
                        <>
                            <Table responsive striped bordered hover size="sm" className="align-middle">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Technischer Platzhalter</th>
                                        <th>Quelle</th>
                                        <th className="text-center" style={{ width: 70 }}>Kopieren</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {placeholders.length === 0 ? (
                                        <tr>
                                            <td colSpan={4} className="text-center text-muted py-4">
                                                Keine Platzhalter gefunden.
                                            </td>
                                        </tr>
                                    ) : (
                                        placeholders.map((item) => {
                                            const key = item.platzhalter_schluessel;
                                            const name = item.beschreibung && item.beschreibung.trim()
                                                ? item.beschreibung
                                                : key;
                                            const isCopied = copiedKey === key;
                                            return (
                                                <tr key={item.platzhalter_id}>
                                                    <td>{name}</td>
                                                    <td>
                                                        <code style={{ fontSize: '0.85em' }}>{key}</code>
                                                    </td>
                                                    <td>
                                                        {item.quell_tabelle && (
                                                            <Badge bg="light" text="dark">{item.quell_tabelle}</Badge>
                                                        )}
                                                    </td>
                                                    <td className="text-center">
                                                        <OverlayTrigger
                                                            placement="top"
                                                            overlay={<Tooltip>{isCopied ? 'Kopiert!' : 'Technischen Namen kopieren'}</Tooltip>}
                                                        >
                                                            <Button
                                                                variant="link"
                                                                size="sm"
                                                                className="p-0"
                                                                onClick={() => handleCopy(key)}
                                                                aria-label="Technischen Platzhalter-Namen kopieren"
                                                                style={{ lineHeight: 1 }}
                                                            >
                                                                {isCopied
                                                                    ? <FaCheck size={12} className="text-success" />
                                                                    : <FaRegCopy size={12} />}
                                                            </Button>
                                                        </OverlayTrigger>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </Table>

                            {totalPages > 1 && (
                                <Pagination className="mb-0">
                                    <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                                    <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} />
                                    <Pagination.Item active>{currentPage}</Pagination.Item>
                                    <Pagination.Next onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} />
                                    <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
                                    <span className="ms-2 align-self-center text-muted">Seite {currentPage} / {totalPages}</span>
                                </Pagination>
                            )}
                        </>
                    )}
                </Card.Body>
            </Card>
        </Container>
    );
};

export default BoardPlaceholderSync;
